/**
 * Minimal localStorage wrapper that is a drop-in replacement for the
 * (unmaintained) `lockr` package. It deliberately preserves lockr's on-disk
 * format so values persisted by previous deploys -- including the auth token,
 * table sort/page-size prefs and pending invites -- remain readable:
 *
 *   - keys are stored under `${prefix}${key}`
 *   - values are JSON-serialized as `{ "data": <value> }`
 *   - `get` unwraps `.data`, falling back to a raw string for legacy/non-JSON
 *     entries, and returns the `missing` default when the key is absent
 *   - `flush` clears only prefixed keys (or everything when no prefix is set)
 *
 * Only the surface the app actually uses is implemented: prefix, get, set, rm,
 * flush.
 */
class LocalStore {
  prefix = '';

  prefixedKey(key: string): string {
    return `${this.prefix || ''}${key}`;
  }

  set(key: string, value: unknown): void {
    try {
      localStorage.setItem(this.prefixedKey(key), JSON.stringify({ data: value }));
    } catch {
      // Match lockr: swallow quota / unavailable-storage errors.
    }
  }

  get(key: string, missing?: unknown): unknown {
    const queryKey = this.prefixedKey(key);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSON.parse boundary; value shape is unknown until unwrapped below
    let value: any;

    try {
      // `getItem` may return null; cast preserves lockr's `JSON.parse(null) -> null` behavior.
      value = JSON.parse(localStorage.getItem(queryKey) as string);
    } catch {
      value = localStorage[queryKey] ? { data: localStorage.getItem(queryKey) } : null;
    }

    if (value === null) {
      return missing;
    }
    if (typeof value === 'object' && typeof value.data !== 'undefined') {
      return value.data;
    }
    return missing;
  }

  rm(key: string): void {
    localStorage.removeItem(this.prefixedKey(key));
  }

  flush(): void {
    if (this.prefix && this.prefix.length) {
      Object.keys(localStorage).forEach((key) => {
        if (key.indexOf(this.prefix) === 0) {
          localStorage.removeItem(key);
        }
      });
    } else {
      localStorage.clear();
    }
  }
}

// Single shared instance, matching lockr's module-singleton behavior so that
// setting `.prefix` once (in App.js) applies everywhere.
export default new LocalStore();
